import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaPlus, FaTrash, FaEdit, FaEye, FaHistory, FaClock, FaStar, FaCopy } from 'react-icons/fa';
import { historyApi, pageApi } from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Sidebar.css';
const Sidebar = ({
  pages = [],
  onCreatePage,
  onDeletePage,
  onRefreshPages,
  isOpen = true,
  onToggle,
  onPageSelect,
  user, onLogout,
}) => {
  const { slug: currentPageSlug } = useParams();
  const { user: authUser } = useAuth();
  const currentUser = user || authUser;
  const [newPageTitle, setNewPageTitle] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'favorites', 'recent', 'timeline'
  const [timelineData, setTimelineData] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [expandedTimelinePages, setExpandedTimelinePages] = useState({});
  const navigate = useNavigate();
  useEffect(() => {
    if (viewMode === 'timeline' && pages.length > 0) {
      loadTimelineData();
    }
  }, [viewMode, pages]);

  // Listen for pages updated event
  useEffect(() => {
    const handlePagesUpdated = async () => {
      if (onRefreshPages) {
        await onRefreshPages();
      }
    };

    window.addEventListener('pagesUpdated', handlePagesUpdated);
    return () => {
      window.removeEventListener('pagesUpdated', handlePagesUpdated);
    };
  }, [onRefreshPages]);

  const handleCreatePage = () => {
    if (newPageTitle.trim()) {
      onCreatePage(newPageTitle.trim());
      setNewPageTitle('');
    } else {
      onCreatePage('Untitled');
    }
  };

  const handleDuplicatePage = async (e, page) => {
    e.stopPropagation();
    try {
      const response = await pageApi.duplicate(page.id);
      // Refresh the pages list
      if (onCreatePage) {
        // Call the parent's refresh or recreate mechanism
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to duplicate page:', error);
      alert('Failed to duplicate page. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCreatePage();
    }
  };

  // Add these states at the top of your component
  const [timelinePagination, setTimelinePagination] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState({});

  // Update loadTimelineData function
  const loadTimelineData = async (loadMore = false, pageId = null) => {
    if (!loadMore) {
      setTimelineLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const pagesToLoad = pageId
        ? pages.filter(page => page.id === pageId)
        : pages;

      const timelinePromises = pagesToLoad.map(async (page) => {
        try {
          const cursor = loadMore && timelinePagination[page.id]?.nextCursor
            ? timelinePagination[page.id].nextCursor
            : null;

          const response = await historyApi.getTimelineEntries(page.id, {
            limit: 10,
            cursor: cursor
          });

          return {
            page,
            entries: response.data || [],
            pagination: response.pagination || {}
          };
        } catch (error) {
          console.error(`Failed to load timeline for page ${page.id}:`, error);
          return {
            page,
            entries: [],
            pagination: {}
          };
        }
      });

      const results = await Promise.all(timelinePromises);

      if (loadMore) {
        // Merge new entries with existing ones - FIXED VERSION
        setTimelineData(prevData => {
          const newData = [...prevData];
          results.forEach(result => {
            const existingIndex = newData.findIndex(item => item.page.id === result.page.id);
            if (existingIndex >= 0) {
              // Create a Set of existing entry IDs to avoid duplicates
              const existingEntryIds = new Set(
                newData[existingIndex].entries.map(entry => entry.id)
              );

              // Filter out entries that already exist
              const newUniqueEntries = result.entries.filter(
                entry => !existingEntryIds.has(entry.id)
              );

              // Append only unique new entries
              newData[existingIndex].entries = [
                ...newData[existingIndex].entries,
                ...newUniqueEntries
              ];

              // Update pagination state
              setTimelinePagination(prev => ({
                ...prev,
                [result.page.id]: result.pagination
              }));

              // Update hasMore state
              setHasMore(prev => ({
                ...prev,
                [result.page.id]: result.pagination.hasMore
              }));
            }
          });
          return newData;
        });
      } else {
        // Initial load
        const newData = results.map(result => ({
          page: result.page,
          entries: result.entries
        }));

        setTimelineData(newData);

        // Set pagination state
        const paginationState = {};
        const hasMoreState = {};
        results.forEach(result => {
          paginationState[result.page.id] = result.pagination;
          hasMoreState[result.page.id] = result.pagination.hasMore;
        });
        setTimelinePagination(paginationState);
        setHasMore(hasMoreState);
      }
    } catch (error) {
      console.error('Failed to load timeline data:', error);
    } finally {
      setTimelineLoading(false);
      setLoadingMore(false);
    }
  };

  // Update load more handler to accept pageId
  const handleLoadMore = (pageId) => {
    loadTimelineData(true, pageId);
  };

  // // Add load more handler
  // const handleLoadMore = (pageId) => {
  //   loadTimelineData(true);
  // };

  // Load initial data in useEffect
  useEffect(() => {
    if (pages.length > 0) {
      loadTimelineData(false);
    }
  }, [pages]);

  const formatTimeAgo = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;

      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHours = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 7) {
        return date.toLocaleDateString();
      } else if (diffDays > 0) {
        return diffDays === 1 ? 'Yesterday' : `${diffDays} days ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMin > 0) {
        return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
      } else {
        return 'Just now';
      }
    } catch (error) {
      return 'Recently';
    }
  };

  const toggleTimelinePage = (pageId) => {
    setExpandedTimelinePages(prev => ({
      ...prev,
      [pageId]: !prev[pageId]
    }));
  };

  const handleViewHistory = (entry, page) => {
    if (entry.id && page.slug) {
      navigate(`/page/edit/${page.slug}?historyId=${entry.id}`);
    }
  };

  const renderTimelineView = () => {
    if (timelineLoading && !loadingMore) {
      return (
        <div className="timeline-loading">
          <div className="loading-spinner"></div>
          <p>Loading timeline...</p>
        </div>
      );
    }

    if (timelineData.length === 0) {
      return (
        <div className="empty-timeline">
          <div className="empty-icon">
            <FaHistory size={48} />
          </div>
          <h3>No History Yet</h3>
          <p>Save your pages to see timeline</p>
        </div>
      );
    }

    return (
      <ul className="timeline-list">
        {timelineData.map(({ page, entries }) => (
          <li key={page.id} className="timeline-page-item">
            <div
              className="timeline-page-header"
              onClick={() => toggleTimelinePage(page.id)}
            >
              <div className="page-icon-title">
                {/* <span className="page-icon-small">{page.icon || '📄'}</span> */}
                <span className="page-title-timeline">{page.title || 'Untitled'}</span>
              </div>
              <div className="timeline-stats">
                <span className="entry-count">
                  {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
                </span>
                <span className={`toggle-icon ${expandedTimelinePages[page.id] ? 'expanded' : ''}`}>
                  ▼
                </span>
              </div>
            </div>

            {expandedTimelinePages[page.id] && (
              <>
                <ul className="page-entries-list">
                  {entries.map((entry, index) => (
                    <li
                      key={entry.id || index}
                      className={`
                      timeline-entry 
                      ${entry.operation === 'create' ? 'create-operation' : ''}
                      ${entry.operation === 'update' ? 'update-operation' : ''}
                      ${entry.operation === 'delete' ? 'delete-operation' : ''}
                      ${entry.operation === 'snapshot' ? 'snapshot-operation' : ''}
                    `}
                      onClick={() => handleViewHistory(entry, page)}
                    >
                      <div className="entry-time">
                        <FaClock size={12} />
                        <span className="time-text">{entry.time}</span>
                        <span className="time-ago">{formatTimeAgo(entry.timestamp)}</span>
                      </div>
                      <div className="entry-details">
                        <div className="entry-operation">
                          <span className="operation-badge">
                            {entry.operation_text}
                          </span>
                          {entry.has_snapshot && (
                            <span className="snapshot-badge">Page Save</span>
                          )}
                        </div>

                        {entry.preview.length > 0 && (
                          <div className="entry-blocks">
                            {entry.preview.map((type, idx) => (
                              <span key={idx} className="block-type-tag">
                                {type}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="entry-actions">
                        <button
                          className="action-btn view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewHistory(entry, page);
                          }}
                          title="View this version"
                        >
                          <FaEye size={12} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Actual Load More Button */}
                {hasMore[page.id] && (
                  <div className="timeline-load-more">
                    <button
                      className="load-more-btn"
                      onClick={() => handleLoadMore(page.id)}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <div className="small-spinner"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <FaPlus size={12} />
                          Load More
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    );
  };

  // Filter pages based on view mode
  const filteredPages = pages.filter(page => {
    if (viewMode === 'favorites') return page.favorite;
    if (viewMode === 'recent') {
      const pageDate = new Date(page.updated_at || page.created_at);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return pageDate > weekAgo;
    }
    return true;
  });

  // Sort pages: favorites first, then by date
  const sortedPages = [...filteredPages].sort((a, b) => {
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;
    return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
  });

  return (
    <>
      {/* Sidebar Toggle Button */}
      <button
        className="sidebar-toggle"
        onClick={onToggle}
        style={{
          left: isOpen ? '270px' : '20px' /* Move toggle with sidebar */
        }}
      >
        {isOpen ? <FaTimes /> : <FaBars />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="brand-icon">📝</div>
            <h1>Report Generator</h1>
          </div>
          <p className="subtitle">Your personal workspace</p>
        </div>

        {/* Create Page Section */}
        <div className="create-page-section">
          <div className="create-page-input">
            <input
              type="text"
              placeholder="Create a new page..."
              value={newPageTitle}
              onChange={(e) => setNewPageTitle(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button
              className="create-page-btn"
              onClick={handleCreatePage}
              disabled={!newPageTitle.trim()}
            >
              <FaPlus />
            </button>
          </div>
          <button
            className="quick-create-btn"
            onClick={() => onCreatePage('Untitled')}
          >
            <FaPlus /> Quick Create
          </button>
        </div>

        {/* View Mode Tabs */}
        <div className="view-mode-tabs">
          <button
            className={`tab-btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode('all')}
          >
            All Pages
          </button>
          {/* <button
            className={`tab-btn ${viewMode === 'favorites' ? 'active' : ''}`}
            onClick={() => setViewMode('favorites')}
          >
            <FaStar size={12} /> Favorites
          </button>
          <button
            className={`tab-btn ${viewMode === 'recent' ? 'active' : ''}`}
            onClick={() => setViewMode('recent')}
          >
            <FaClock size={12} /> Recent
          </button> */}
          <button
            className={`tab-btn ${viewMode === 'timeline' ? 'active' : ''}`}
            onClick={() => setViewMode('timeline')}
          >
            <FaHistory size={12} /> Timeline
          </button>
        </div>

        {/* Page List or Timeline */}
        <div className="pages-section">
          <div className="section-header">
            <div className="section-title">
              <h3>
                {viewMode === 'all' && 'All Pages'}
                {viewMode === 'favorites' && 'Favorites'}
                {viewMode === 'recent' && 'Recent'}
                {viewMode === 'timeline' && 'Page History'}
              </h3>
              <span className="page-count">
                {viewMode === 'timeline'
                  ? timelineData.length
                  : sortedPages.length}
                {viewMode === 'timeline' ? ' pages' : sortedPages.length === 1 ? ' page' : ' pages'}
              </span>
            </div>
          </div>

          {viewMode === 'timeline' ? (
            renderTimelineView()
          ) : sortedPages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                {viewMode === 'favorites' ? '⭐' : viewMode === 'recent' ? '🕒' : '📄'}
              </div>
              <p>
                {viewMode === 'favorites' ? 'No favorite pages yet' :
                  viewMode === 'recent' ? 'No recent pages' :
                    'No pages yet'}
              </p>
              {viewMode !== 'all' && (
                <button
                  className="view-all-btn"
                  onClick={() => setViewMode('all')}
                >
                  View All Pages
                </button>
              )}
              <button
                className="create-first-page-btn"
                onClick={() => onCreatePage('My First Page')}
              >
                Create Your First Page
              </button>
            </div>
          ) : (
            <ul className="page-list">
              {sortedPages.map((page) => (
                <li
                  key={page.id}
                  className={`page-item ${page.slug === currentPageSlug ? 'active' : ''}`}
                  onClick={() => onPageSelect && onPageSelect(page.slug)}
                >
                  <div className="page-item-main">
                    <div className="page-icon-wrapper">
                      <div className="page-icon-sidebar">
                        {page.icon ? (
                          // Display page.icon if it exists (emoji, image URL, or data URL)
                          page.icon.startsWith('data:') ||
                            page.icon.startsWith('http') ||
                            page.icon.startsWith('blob:') ? (
                            <img src={page.icon} alt="Page icon" />
                          ) : (
                            <span className="page-icon-emoji">{page.icon}</span>
                          )
                        ) : page.cover_image ? (
                          // If no icon but has cover image, show image icon
                          page.cover_image.startsWith('data:') ||
                            page.cover_image.startsWith('http') ||
                            page.cover_image.startsWith('blob:') ? (
                            <img src={page.cover_image} alt="Cover" />
                          ) : (
                            <span className="page-icon-emoji">🖼️</span>
                          )
                        ) : (
                          // Default document icon
                          <span className="page-icon-emoji">📄</span>
                        )}
                      </div>
                      {page.favorite && (
                        <div className="favorite-indicator">
                          <FaStar size={10} />
                        </div>
                      )}
                    </div>
                    <div className="page-info">
                      <div className="page-title">{page.title || 'Untitled'}</div>
                      <div className="page-meta">
                        <span className="user-name">
                          {page.user_id === currentUser.id
                            ? 'You'
                            : page.user_name
                              ? (page.user_name.length > 20
                                ? page.user_name.substring(0, 20) + '...'
                                : page.user_name
                              )
                              : 'Unknown User'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="page-actions">
                    <button
                      className="action-btn view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/page/${page.slug}`);
                      }}
                      title="View Page"
                    >
                      <FaEye />
                    </button>
                    {
                      page.user_id === currentUser.id && (
                        <>
                          <button
                            className="action-btn edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/page/edit/${page.slug}`);
                            }}
                            title="Edit Page"
                            disabled={page.user_id !== currentUser.id}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="action-btn duplicate-btn"
                            onClick={(e) => handleDuplicatePage(e, page)}
                            title="Duplicate Page"
                          >
                            <FaCopy />
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeletePage(page.id);
                            }}
                            title="Delete Page"
                          >
                            <FaTrash />
                          </button>
                        </>
                      )
                    }
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* User Info */}
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="avatar">
              {currentUser.avatar_url ? (
                <img src={currentUser.avatar_url} alt={currentUser.display_name} />
              ) : (
                <div className="avatar-placeholder">
                  {currentUser.display_name?.charAt(0) || currentUser.username?.charAt(0)}
                </div>
              )}
            </div>
            <div className="user-details">
              <div className="username">{currentUser.display_name || currentUser.username}</div>
              <div className="email">{currentUser.email}</div>
            </div>
          </div>
          <div className="footer-actions">
            <button className="logout-btn" onClick={onLogout}>Logout</button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;